import { AlertType } from '@types';
import { AlertModal, Card, PageHeader } from '_atoms';
import { AdminService } from '_services';
import React, { useCallback, useEffect, useState } from 'react';
import {
  MdCake,
  MdClose,
  MdEdit,
  MdExpandLess,
  MdExpandMore,
  MdSave,
} from 'react-icons/md';
import { PartyExtra, PartySession } from '../../@types';

const Parties: React.FC = () => {
  const [partySessions, setPartySessions] = useState<PartySession[]>([]);
  const [partyExtras, setPartyExtras] = useState<PartyExtra[]>([]);
  const [expandedSessions, setExpandedSessions] = useState<Set<string>>(
    new Set(),
  );
  const [expandedExtras, setExpandedExtras] = useState<Set<string>>(new Set());
  const [editingSessions, setEditingSessions] = useState<Set<string>>(
    new Set(),
  );
  const [editingExtras, setEditingExtras] = useState<Set<string>>(new Set());
  const [isLoadingSave, setIsLoadingSave] = useState(false);
  const [isLoadingInitial, setIsLoadingInitial] = useState(true);
  const [alertModal, setAlertModal] = useState<{
    isOpen: boolean;
    type: AlertType;
    title: string;
    message: string;
  }>({
    isOpen: false,
    type: 'info',
    title: '',
    message: '',
  });

  const showAlert = useCallback(
    (type: AlertType, title: string, message: string) => {
      setAlertModal({
        isOpen: true,
        type,
        title,
        message,
      });
    },
    [],
  );

  const closeAlert = () => {
    setAlertModal(prev => ({ ...prev, isOpen: false }));
  };

  const handleSessionChange = (
    sessionId: string,
    field: keyof PartySession,
    value: string | number | boolean,
  ) => {
    setPartySessions(prev =>
      prev.map(session =>
        session.id === sessionId ? { ...session, [field]: value } : session,
      ),
    );
  };

  const handleExtraChange = (
    extraId: string,
    field: keyof PartyExtra,
    value: string | number,
  ) => {
    setPartyExtras(prev =>
      prev.map(extra =>
        extra.id === extraId ? { ...extra, [field]: value } : extra,
      ),
    );
  };

  const handleSessionEditToggle = (sessionId: string) => {
    setEditingSessions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sessionId)) {
        newSet.delete(sessionId);
      } else {
        newSet.add(sessionId);
        setExpandedSessions(prevExpanded => {
          const newExpandedSet = new Set(prevExpanded);
          newExpandedSet.add(sessionId);
          return newExpandedSet;
        });
      }
      return newSet;
    });
  };

  const handleExtraEditToggle = (extraId: string) => {
    setEditingExtras(prev => {
      const newSet = new Set(prev);
      if (newSet.has(extraId)) {
        newSet.delete(extraId);
      } else {
        newSet.add(extraId);
        setExpandedExtras(prevExpanded => {
          const newExpandedSet = new Set(prevExpanded);
          newExpandedSet.add(extraId);
          return newExpandedSet;
        });
      }
      return newSet;
    });
  };

  const handleSessionExpandToggle = (sessionId: string) => {
    setExpandedSessions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sessionId)) {
        newSet.delete(sessionId);
      } else {
        newSet.add(sessionId);
      }
      return newSet;
    });
  };

  const handleExtraExpandToggle = (extraId: string) => {
    setExpandedExtras(prev => {
      const newSet = new Set(prev);
      if (newSet.has(extraId)) {
        newSet.delete(extraId);
      } else {
        newSet.add(extraId);
      }
      return newSet;
    });
  };

  const handleSaveSession = useCallback(
    async (session: PartySession) => {
      setIsLoadingSave(true);

      try {
        await AdminService.updatePartySession(session);
        showAlert('success', 'Success', 'Party session updated successfully!');
        setEditingSessions(prev => {
          const newSet = new Set(prev);
          newSet.delete(session.id);
          return newSet;
        });
      } catch (error) {
        console.error('Error updating party session:', error);
        showAlert(
          'error',
          'Error',
          'Error updating party session. Please try again.',
        );
      } finally {
        setIsLoadingSave(false);
      }
    },
    [showAlert],
  );

  const handleSaveExtra = useCallback(
    async (extra: PartyExtra) => {
      setIsLoadingSave(true);

      try {
        await AdminService.updatePartyExtra(extra);
        showAlert('success', 'Success', 'Party extra updated successfully!');
        setEditingExtras(prev => {
          const newSet = new Set(prev);
          newSet.delete(extra.id);
          return newSet;
        });
      } catch (error) {
        console.error('Error updating party extra:', error);
        showAlert(
          'error',
          'Error',
          'Error updating party extra. Please try again.',
        );
      } finally {
        setIsLoadingSave(false);
      }
    },
    [showAlert],
  );

  // Load party data on page load
  useEffect(() => {
    const loadPartyData = async () => {
      setIsLoadingInitial(true);
      try {
        const [sessionsResponse, extrasResponse] = await Promise.all([
          AdminService.getPartySessions(),
          AdminService.getPartyExtras(),
        ]);

        setPartySessions(sessionsResponse as unknown as PartySession[]);
        setPartyExtras(extrasResponse as unknown as PartyExtra[]);
      } catch (error) {
        console.error('Error loading party data:', error);
        showAlert('error', 'Error', 'Failed to load party data.');
      } finally {
        setIsLoadingInitial(false);
      }
    };

    loadPartyData();
  }, [showAlert]);

  return (
    <div className="space-y-8">
      {/* Header */}
      <PageHeader
        icon={MdCake}
        title="Party Management"
        description="Configure party sessions, extras, pricing, and availability settings."
      />

      {/* Main Content */}
      {isLoadingInitial ? (
        <Card>
          <div className="flex items-center justify-center py-8">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading party data...</p>
            </div>
          </div>
        </Card>
      ) : (
        <div className="space-y-8">
          {/* Party Sessions Section */}
          <div>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              Party Sessions
            </h2>
            {partySessions.length === 0 ? (
              <Card>
                <div className="text-center py-8">
                  <p className="text-gray-600">No party sessions found.</p>
                </div>
              </Card>
            ) : (
              <div className="space-y-4">
                {partySessions.map(session => {
                  const isExpanded = expandedSessions.has(session.id);
                  const isEditing = editingSessions.has(session.id);

                  return (
                    <Card key={session.id} className="flex flex-col">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <button
                            onClick={() =>
                              handleSessionExpandToggle(session.id)
                            }
                            className="p-1 hover:bg-gray-100 rounded transition-colors"
                          >
                            {isExpanded ? (
                              <MdExpandLess className="w-5 h-5 text-gray-600" />
                            ) : (
                              <MdExpandMore className="w-5 h-5 text-gray-600" />
                            )}
                          </button>
                          <div>
                            <h3 className="text-lg font-medium text-gray-900">
                              {session.name}
                            </h3>
                            <p className="text-sm text-gray-600">
                              {session.type}
                            </p>
                            <div className="flex items-center space-x-4 mt-1">
                              <span className="text-xs text-gray-500">
                                Price: ${session.price}
                              </span>
                              <span className="text-xs text-gray-500">
                                Pizza Limit: {session.pizzaLimit}
                              </span>
                              <span
                                className={`text-xs px-2 py-1 rounded-full ${
                                  session.isActive
                                    ? 'bg-green-100 text-green-800'
                                    : 'bg-red-100 text-red-800'
                                }`}
                              >
                                {session.isActive
                                  ? 'Active'
                                  : 'Archived / Inactive'}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleSessionEditToggle(session.id)}
                            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                          >
                            {isEditing ? (
                              <MdClose className="mr-1" />
                            ) : (
                              <MdEdit className="mr-1" />
                            )}
                            {isEditing ? 'Cancel' : 'Edit'}
                          </button>
                        </div>
                      </div>

                      {isExpanded && (
                        <div className="mt-4 space-y-4">
                          {isEditing ? (
                            // Edit view
                            <div className="space-y-4">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  Name
                                </label>
                                <input
                                  type="text"
                                  value={session.name}
                                  onChange={e =>
                                    handleSessionChange(
                                      session.id,
                                      'name',
                                      e.target.value,
                                    )
                                  }
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                />
                              </div>

                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  Description
                                </label>
                                <textarea
                                  value={session.description}
                                  onChange={e =>
                                    handleSessionChange(
                                      session.id,
                                      'description',
                                      e.target.value,
                                    )
                                  }
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-h-[100px] resize-y"
                                />
                              </div>

                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Price ($)
                                  </label>
                                  <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={session.price}
                                    onChange={e =>
                                      handleSessionChange(
                                        session.id,
                                        'price',
                                        parseFloat(e.target.value) || 0,
                                      )
                                    }
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                  />
                                </div>

                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Status
                                  </label>
                                  <div className="flex items-center">
                                    <button
                                      type="button"
                                      onClick={() =>
                                        handleSessionChange(
                                          session.id,
                                          'isActive',
                                          !session.isActive,
                                        )
                                      }
                                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                                        session.isActive
                                          ? 'bg-blue-600'
                                          : 'bg-gray-200'
                                      }`}
                                    >
                                      <span
                                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                          session.isActive
                                            ? 'translate-x-6'
                                            : 'translate-x-1'
                                        }`}
                                      />
                                    </button>
                                    <span className="ml-3 text-sm text-gray-700">
                                      {session.isActive
                                        ? 'Active'
                                        : 'Archived / Inactive'}
                                    </span>
                                  </div>
                                </div>
                              </div>

                              {/* Save Button */}
                              <div className="flex justify-end">
                                <button
                                  type="button"
                                  onClick={() => handleSaveSession(session)}
                                  disabled={
                                    isLoadingSave ||
                                    !session.name?.trim() ||
                                    !session.description?.trim() ||
                                    session.price < 0 ||
                                    session.pizzaLimit < 0
                                  }
                                  className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                                >
                                  <MdSave className="mr-2" />
                                  {isLoadingSave ? 'Saving...' : 'Save Session'}
                                </button>
                              </div>
                            </div>
                          ) : (
                            // Read-only view
                            <div className="space-y-4">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  Description
                                </label>
                                <p className="text-sm text-gray-600">
                                  {session.description}
                                </p>
                              </div>

                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  Disclaimer
                                </label>
                                <p className="text-sm text-gray-600">
                                  {session.disclaimer}
                                </p>
                              </div>

                              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Price
                                  </label>
                                  <p className="text-sm text-gray-600">
                                    ${session.price}
                                  </p>
                                </div>

                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Pizza Limit
                                  </label>
                                  <p className="text-sm text-gray-600">
                                    {session.pizzaLimit}
                                  </p>
                                </div>

                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Status
                                  </label>
                                  <span
                                    className={`text-sm px-2 py-1 rounded-full ${
                                      session.isActive
                                        ? 'bg-green-100 text-green-800'
                                        : 'bg-red-100 text-red-800'
                                    }`}
                                  >
                                    {session.isActive
                                      ? 'Active'
                                      : 'Archived / Inactive'}
                                  </span>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </Card>
                  );
                })}
              </div>
            )}
          </div>

          {/* Party Extras Section */}
          <div>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              Party Extras
            </h2>
            {partyExtras.length === 0 ? (
              <Card>
                <div className="text-center py-8">
                  <p className="text-gray-600">No party extras found.</p>
                </div>
              </Card>
            ) : (
              <div className="space-y-4">
                {partyExtras.map(extra => {
                  const isExpanded = expandedExtras.has(extra.id);
                  const isEditing = editingExtras.has(extra.id);

                  return (
                    <Card key={extra.id} className="flex flex-col">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <button
                            onClick={() => handleExtraExpandToggle(extra.id)}
                            className="p-1 hover:bg-gray-100 rounded transition-colors"
                          >
                            {isExpanded ? (
                              <MdExpandLess className="w-5 h-5 text-gray-600" />
                            ) : (
                              <MdExpandMore className="w-5 h-5 text-gray-600" />
                            )}
                          </button>
                          {extra.photoUrl && (
                            <div className="flex-shrink-0">
                              <img
                                src={extra.photoUrl}
                                alt={extra.name}
                                className="w-16 h-16 object-cover rounded-lg border border-gray-200"
                                onError={e => {
                                  e.currentTarget.style.display = 'none';
                                }}
                              />
                            </div>
                          )}
                          <div>
                            <h3 className="text-lg font-medium text-gray-900">
                              {extra.name}
                            </h3>
                            <p className="text-sm text-gray-600">
                              {extra.type}
                            </p>
                            <div className="flex items-center space-x-4 mt-1">
                              <span className="text-xs text-gray-500">
                                Price: ${extra.price}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleExtraEditToggle(extra.id)}
                            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                          >
                            {isEditing ? (
                              <MdClose className="mr-1" />
                            ) : (
                              <MdEdit className="mr-1" />
                            )}
                            {isEditing ? 'Cancel' : 'Edit'}
                          </button>
                        </div>
                      </div>

                      {isExpanded && (
                        <div className="mt-4 space-y-4">
                          {isEditing ? (
                            // Edit view
                            <div className="space-y-4">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  Name
                                </label>
                                <input
                                  type="text"
                                  value={extra.name}
                                  onChange={e =>
                                    handleExtraChange(
                                      extra.id,
                                      'name',
                                      e.target.value,
                                    )
                                  }
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                />
                              </div>

                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  Description
                                </label>
                                <textarea
                                  value={extra.description}
                                  onChange={e =>
                                    handleExtraChange(
                                      extra.id,
                                      'description',
                                      e.target.value,
                                    )
                                  }
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-h-[100px] resize-y"
                                />
                              </div>

                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  Price ($)
                                </label>
                                <input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  value={extra.price}
                                  onChange={e =>
                                    handleExtraChange(
                                      extra.id,
                                      'price',
                                      parseFloat(e.target.value) || 0,
                                    )
                                  }
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                />
                              </div>

                              {/* Save Button */}
                              <div className="flex justify-end">
                                <button
                                  type="button"
                                  onClick={() => handleSaveExtra(extra)}
                                  disabled={
                                    isLoadingSave ||
                                    !extra.name?.trim() ||
                                    extra.price < 0
                                  }
                                  className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                                >
                                  <MdSave className="mr-2" />
                                  {isLoadingSave ? 'Saving...' : 'Save Extra'}
                                </button>
                              </div>
                            </div>
                          ) : (
                            // Read-only view
                            <div className="space-y-4">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  Description
                                </label>
                                <p className="text-sm text-gray-600">
                                  {extra.description}
                                </p>
                              </div>

                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Price
                                  </label>
                                  <p className="text-sm text-gray-600">
                                    ${extra.price}
                                  </p>
                                </div>

                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Photo URL
                                  </label>
                                  <p className="text-sm text-gray-600 break-all">
                                    {extra.photoUrl}
                                  </p>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Alert Modal */}
      <AlertModal
        isOpen={alertModal.isOpen}
        onClose={closeAlert}
        type={alertModal.type}
        title={alertModal.title}
        message={alertModal.message}
      />
    </div>
  );
};

export default Parties;
